import { Stack } from '@mui/material';
import type { ReactNode } from 'react';

import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { COLORS } from '@/styles/tokens/colors';
import { pluralize } from '@/utils/formatting/strings';

import { EstateOnboardingSuggestionMenuItem } from './EstateOnboardingSuggestionMenuItem';

export interface EstateOnboardingSuggestionOption {
  key: string;
  label: ReactNode;
  count: number;
  onClick: () => void;
}

export interface EstateOnboardingSuggestionsButtonProps
  extends Omit<
    Partial<ButtonWithPopoverProps>,
    'children' | 'iconProps' | 'label' | 'startIcon'
  > {
  dataTestId: string;
  isGenerating?: boolean;
  suggestionOptions: EstateOnboardingSuggestionOption[];
  totalCount: number;
}

export function EstateOnboardingSuggestionsButton({
  dataTestId,
  isGenerating = false,
  suggestionOptions,
  totalCount,
  size = 'xs',
  variant = 'secondary',
  popperProps,
  popperVariant = 'menuBelow',
  sx,
  ...buttonWithPopoverProps
}: EstateOnboardingSuggestionsButtonProps) {
  const canEdit = useCanEditHouseholdData();

  if (!canEdit) {
    return null;
  }

  if (!isGenerating && totalCount === 0) {
    return null;
  }

  if (totalCount === 0) {
    return <GeneratingIndicator dataTestId={dataTestId} />;
  }

  const label = pluralize({ count: totalCount, singular: 'suggestion' });
  const buttonSx = {
    ...sx,
    minWidth: '148px',
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {isGenerating && <GeneratingIndicator dataTestId={dataTestId} />}
      <ButtonWithPopover
        data-testid={dataTestId}
        label={label}
        size={size}
        variant={variant}
        popperVariant={popperVariant}
        startIcon={Stars01Icon}
        iconProps={{
          color: COLORS.TEAL[600],
          size: 16,
        }}
        popperProps={{
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          transformOrigin: { horizontal: 'left', vertical: 0 },
          ...popperProps,
        }}
        sx={buttonSx}
        {...buttonWithPopoverProps}
      >
        {suggestionOptions.map(
          ({ key, label: optionLabel, count, onClick }) => (
            <EstateOnboardingSuggestionMenuItem
              key={key}
              label={optionLabel}
              count={count}
              onClick={onClick}
            />
          )
        )}
      </ButtonWithPopover>
    </Stack>
  );
}
